/* One lock for the paired buy box/cart. Never queue or retry purchase intents. */
(function () {
  if (window.TeaCartSession) return;
  let busy = false;
  let badgeTimer, badgeRevision = 0;
  function scheduleBadgeRefresh() {
    const revision = ++badgeRevision;
    clearTimeout(badgeTimer);
    if (!document.getElementById('cart-icon-bubble')) return;
    badgeTimer = setTimeout(async () => {
      try {
        // Let Shopify render the count, empty-cart icon and accessible label.
        // Preserve the outer anchor and its cart-opening event listeners.
        const cartUrl = window.routes?.cart_url || '/cart';
        const response = await fetch(`${cartUrl}?section_id=cart-icon-bubble`, { cache: 'no-store' });
        if (!response.ok) return;
        const html = await response.text();
        if (revision !== badgeRevision) return;
        const source = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
        const target = document.getElementById('cart-icon-bubble');
        if (source && target) target.innerHTML = source.innerHTML;
      } catch (_) { /* A badge refresh must never fail or retry a purchase. */ }
    }, 100);
  }
  // Includes native Rebuy quantity/removal and automatic gift/protection changes.
  ['rebuy:cart.change', 'rebuy:cart.enriched', 'rebuy:smartcart.ready', 'rebuy:smartcart.show']
    .forEach(name => document.addEventListener(name, scheduleBadgeRefresh));
  function syncRebuy() {
    // SmartCart.setCart updates presentation only. Publishing through Rebuy's
    // Cart lifecycle also runs its native gift/eligibility processing. Do not
    // pre-set SmartCart: that erases the old/new delta used by its change handler.
    return new Promise((resolve, reject) => {
      const cart = window.Rebuy?.Cart;
      if (typeof cart?.fetchShopifyCart !== 'function') {
        reject(new Error('The cart is still loading. Please refresh before continuing.'));
        return;
      }
      const timeout = setTimeout(() => reject(new Error('Cart synchronization timed out. Please refresh before continuing.')), 15000);
      try {
        cart.fetchShopifyCart(result => { clearTimeout(timeout); scheduleBadgeRefresh(); resolve(result); });
      } catch (error) { clearTimeout(timeout); reject(error); }
    });
  }
  async function syncNative(cartState, trigger) {
    const unavailable = code => {
      const error = new Error('Cart preview could not be refreshed.');
      error.code = code;
      return error;
    };
    const drawer = document.querySelector('cart-drawer');
    if (!drawer || typeof drawer.getSectionsToRender !== 'function' || typeof drawer.renderContents !== 'function') {
      scheduleBadgeRefresh();
      return false;
    }
    if (!Number.isSafeInteger(cartState?.item_count) || !Array.isArray(cartState.items)) throw unavailable('NATIVE_CART_STATE');
    const sectionsToRender = drawer.getSectionsToRender();
    if (!Array.isArray(sectionsToRender) || !sectionsToRender.length || sectionsToRender.length > 5 ||
        sectionsToRender.some(section => typeof section.id !== 'string' || !/^[A-Za-z0-9_-]+$/.test(section.id))) throw unavailable('NATIVE_CART_SECTION_CONFIG');
    const root = window.Shopify?.routes?.root || '/';
    if (typeof root !== 'string' || !root.startsWith('/') || root.startsWith('//') || /[?#]/.test(root)) throw unavailable('NATIVE_CART_SECTION_CONFIG');
    const response = await fetch(root + '?sections=' + sectionsToRender.map(section => section.id).join(','), {
      credentials: 'same-origin', cache: 'no-store', signal: AbortSignal.timeout(15000)
    });
    if (!response.ok) throw unavailable('NATIVE_CART_HTTP');
    const sections = await response.json();
    for (const section of sectionsToRender) {
      const html = sections?.[section.id];
      const target = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      if (typeof html !== 'string') throw unavailable(Object.prototype.hasOwnProperty.call(sections || {}, section.id)
        ? 'NATIVE_CART_SECTION_NULL' : 'NATIVE_CART_SECTION_MISSING');
      if (!target) throw unavailable('NATIVE_CART_TARGET');
      if (!new DOMParser().parseFromString(html, 'text/html').querySelector(section.selector || '.shopify-section')) throw unavailable('NATIVE_CART_MARKUP');
    }
    drawer.setActiveElement?.(trigger);
    drawer.classList.toggle('is-empty', cartState.item_count === 0);
    drawer.renderContents({ id: cartState.items[cartState.items.length - 1]?.variant_id, sections });
    return true;
  }
  // Every cart request from the buy box and the drawer. A write (POST) that got no clear
  // answer — no response, or an unreadable one — may still land later: it is marked
  // `unconfirmed`, and its caller holds the cart (no further writes, no checkout) until a
  // refresh. A write Shopify answered with an error is a plain rejection. A failed read
  // changed nothing.
  async function request(path, body) {
    const root = window.Shopify?.routes?.root || '/';
    const unconfirmed = error => { if (body) error.unconfirmed = true; return error; };
    let response;
    try {
      response = await fetch(root + path, {
        credentials: 'same-origin', cache: 'no-store', signal: AbortSignal.timeout(15000),
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        ...(body ? { method: 'POST', body: JSON.stringify(body) } : {})
      });
    } catch (error) { throw unconfirmed(error); }
    let value;
    try { value = await response.json(); } catch (error) {
      if (!response.ok) throw new Error('Unable to update the cart. Please try again.');
      throw unconfirmed(error);
    }
    if (!response.ok) throw new Error(typeof value.description === 'string' ? value.description : 'Unable to update the cart. Please try again.');
    return value;
  }
  let unconfirmed = false;
  window.TeaCartSession = Object.freeze({
    get busy() { return busy; },
    // Set once a write is unconfirmed; the lock then stays held until the page reloads.
    get unconfirmed() { return unconfirmed; },
    acquire() { if (busy) return false; busy = true; return true; },
    release() { if (!unconfirmed) busy = false; },
    hold() { unconfirmed = true; busy = true; },
    request,
    syncRebuy,
    syncNative
  });
})();
